import React, { useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IconChevronDown, IconPlus } from "icons";
import clsx from "clsx";
import { usePortalLayerZIndex } from "components/Modal/portalLayer";
import { estimateContentHeight, resolveDropdownPlacement } from "./selectPlacement";

export interface ISelectOption {
  id: any;
  title: any;
  value?: any;
  disabled?: boolean;
  description?: string;
  icon?: React.ElementType;
}

export interface SelectRef {
  toggle: () => void;
  close: () => void;
}

interface ISelect {
  options: ISelectOption[];
  onChange: (value: ISelectOption) => void;
  direction?: string;
  forceDirection?: boolean;
  className?: string;
  titlePrefix?: string;
  defaultId?: any;
  disabled?: boolean;
  isLightMode?: boolean;
  fixedLightMode?: boolean;
  dropdownClassName?: string;
  maxHeight?: number;
  searchable?: boolean;
  // When true (with searchable), a search that matches no option shows an "Add …" row that selects the
  // typed value as a new option (tag/combobox style — used for the free-text Fit field).
  creatable?: boolean;
  searchPlaceholder?: string;
  usePortal?: boolean;
  placeholder?: string;
  // When the selected option's id equals `mutedId`, the trigger label renders greyed (placeholder-style).
  // Used by facet filters whose "none/all" option is a real option (e.g. "Select Season" / "Select Gender").
  mutedId?: any;
  onOpen?: () => void;
  // Test hook for e2e: lands on the trigger; options get `<id>-option-<option.id>` and the search box
  // `<id>-search`. Omitted → no attribute rendered (existing call sites unchanged).
  "data-testid"?: string;
}

const SELECT_PORTAL_Z_INDEX = 100000;

// Only one Select dropdown is open at a time app-wide: opening one closes the previously-open one.
let closeActiveSelect: (() => void) | null = null;

// The list's natural height as a BORDER-box, which is what the resolved cap is applied as
// (`box-sizing: border-box` is global). `scrollHeight` is the content box, so using it raw made a
// panel that exactly fits clip its own 1px borders and grow a 2px scrollbar. `offsetHeight -
// clientHeight` is those borders, and it stays correct while the list is capped.
const measureContentHeight = (list: HTMLUListElement | null): number => (list ? list.scrollHeight + (list.offsetHeight - list.clientHeight) : 0);

const Select = forwardRef<SelectRef, ISelect>(
  (
    {
      titlePrefix = "",
      defaultId,
      options,
      onChange,
      direction = "bottom",
      forceDirection = false,
      className,
      disabled,
      fixedLightMode,
      isLightMode = false,
      dropdownClassName,
      maxHeight,
      searchable = false,
      creatable = false,
      searchPlaceholder,
      usePortal = false,
      placeholder,
      mutedId,
      onOpen,
      "data-testid": dataTestId,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [show, setShow] = React.useState(false);
    // Resolve the selected option from `defaultId`. For a CREATABLE select, a controlled value NOT present in
    // `options` (a just-created "+ Add xxx" value) is kept as a synthetic option; otherwise the sync below
    // would fall back to null/options[0] and silently wipe the created selection (REF-5591 — "+ Add" appeared
    // to do nothing).
    const resolveSelectedItem = (id: any): ISelectOption | null =>
      options.find((item) => item.id === id) ?? (creatable && id !== undefined && id !== null && id !== "" ? { id, title: String(id), value: id } : placeholder ? null : options[0] ?? null);
    const [selectedItem, setSelectedItem] = useState<ISelectOption | null>(resolveSelectedItem(defaultId));
    const [searchQuery, setSearchQuery] = useState("");
    // Direction AND height cap are decided once per open, from a real measurement (see onToggle),
    // and never re-derived during render — so the panel can't change sides while you type in its
    // search box. `top/left/width` are portal-only. REF-6158.
    const [placement, setPlacement] = useState({ top: 0, left: 0, width: 0, openAbove: direction === "top", maxHeight: maxHeight ?? 0 });
    const listRef = React.useRef<HTMLUListElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    // Above any Modal layer hosting this Select — see `usePortalLayerZIndex` (REF-6649).
    const portalZIndex = usePortalLayerZIndex(SELECT_PORTAL_Z_INDEX);

    React.useEffect(() => {
      setSelectedItem(resolveSelectedItem(defaultId));
    }, [defaultId, options, placeholder, creatable]);

    // Filter options based on search query
    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchQuery.trim()) {
        return options;
      }

      return options.filter((option) => option.title?.toString().toLowerCase().includes(searchQuery.toLowerCase()));
    }, [options, searchQuery, searchable]);

    const onClose = useCallback(() => {
      setShow(false);
      setSearchQuery("");
      if (closeActiveSelect === onClose) closeActiveSelect = null;
    }, []);

    const onToggle = useCallback(() => {
      if (disabled) return;

      // Closing — clear the shared registry if this Select owned it.
      if (show) {
        setShow(false);
        setSearchQuery("");
        if (closeActiveSelect === onClose) closeActiveSelect = null;

        return;
      }

      // Opening. Only one Select is open at a time — close whichever was open, then register this one.
      // This runs in the event handler (not inside a setShow updater) on purpose: updater callbacks must
      // stay pure, and closing another component's state from inside one is dropped/warned by React —
      // which is exactly why the previously-open dropdown never closed.
      if (closeActiveSelect && closeActiveSelect !== onClose) closeActiveSelect();
      closeActiveSelect = onClose;

      onOpen?.();

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // In the non-portal path the <ul> is always mounted (the closed wrapper is height:0 +
        // overflow-hidden) and the height cap is only applied while open, so it still reports the
        // list's real, uncapped height here. In portal mode nothing is mounted until we open, so
        // fall back to an estimate and let the layout effect below correct it before paint.
        const next = resolveDropdownPlacement({
          spaceBelow: window.innerHeight - rect.bottom,
          spaceAbove: rect.top,
          contentHeight: measureContentHeight(listRef.current) || estimateContentHeight({ optionCount: options.length, searchable }),
          maxHeight,
          preferAbove: direction === "top",
          lockDirection: forceDirection,
        });

        setPlacement({
          ...next,
          top: next.openAbove ? rect.top - 8 : rect.bottom + 8,
          left: rect.left,
          width: rect.width,
        });
      }

      if (searchable) {
        // Focus search input when opening dropdown
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }

      setShow(true);
    }, [disabled, show, options.length, maxHeight, direction, forceDirection, searchable, onOpen, onClose]);

    const resolvePlacement = useCallback(() => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const next = resolveDropdownPlacement({
        spaceBelow: window.innerHeight - rect.bottom,
        spaceAbove: rect.top,
        contentHeight: measureContentHeight(listRef.current),
        maxHeight,
        preferAbove: direction === "top",
        lockDirection: forceDirection,
      });

      setPlacement((previous) =>
        previous.openAbove === next.openAbove && previous.maxHeight === next.maxHeight ? previous : { ...previous, ...next, top: next.openAbove ? rect.top - 8 : rect.bottom + 8 }
      );
    }, [maxHeight, direction, forceDirection]);

    // Portal mode only: re-resolve against the list's real height now that it is mounted (the
    // open-time pass had nothing to measure and used the estimate). useLayoutEffect, so the
    // correction lands before the browser paints — an estimate that guessed the wrong side never
    // becomes visible, and the second pass resolves to the same numbers so it settles immediately.
    //
    // `options.length` is a dep because a portal Select whose data arrives WHILE it is open would
    // otherwise stay pinned to the cap resolved for the empty list — a 40-option list scrolling
    // inside two rows. `filteredOptions` deliberately is NOT a dep: search input must not move the
    // panel while you type.
    React.useLayoutEffect(() => {
      if (!show || !usePortal) return;

      resolvePlacement();
    }, [show, usePortal, options.length, resolvePlacement]);

    // Expose toggle and close methods via ref
    useImperativeHandle(
      ref,
      () => ({
        toggle: onToggle,
        close: onClose,
      }),
      [onToggle, onClose]
    );

    // Clear the global registry if this Select unmounts while open.
    React.useEffect(
      () => () => {
        if (closeActiveSelect === onClose) closeActiveSelect = null;
      },
      [onClose]
    );

    const onSelect = (option: ISelectOption) => {
      setSelectedItem(option);
      onChange(option);
      setSearchQuery(""); // Clear search on select
      onToggle();
    };
    // Custom click outside handler that also checks the portal dropdown
    React.useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const isInsideContainer = containerRef.current?.contains(target);
        const isInsideDropdown = dropdownRef.current?.contains(target);

        // Close via onClose (not a raw setShow) so the single-open registry is released.
        if (!isInsideContainer && !isInsideDropdown) onClose();
      };

      document.addEventListener("click", handleClickOutside);

      return () => document.removeEventListener("click", handleClickOutside);
    }, [onClose]);

    // Portal dropdown stays fixed to the viewport while the trigger scrolls — close it instead of letting it detach.
    React.useEffect(() => {
      if (!show || !usePortal) return;

      const handleViewportChange = (e: Event) => {
        if (e.type === "scroll" && e.target instanceof Node && dropdownRef.current?.contains(e.target)) return;
        onClose();
      };

      document.addEventListener("scroll", handleViewportChange, { capture: true });
      window.addEventListener("resize", handleViewportChange);

      return () => {
        document.removeEventListener("scroll", handleViewportChange, { capture: true });
        window.removeEventListener("resize", handleViewportChange);
      };
    }, [show, usePortal, onClose]);

    // The non-portal panel moves with its trigger, so it survives a scroll — but a RESIZE (window
    // drag, devtools, mobile keyboard, locale/font reflow) changes the room on both sides while the
    // placement stays as resolved at open time. Before REF-6158 the direction was re-derived on
    // every render, so any later render recovered; now it lives in state and needs an explicit
    // re-resolve, or an open dropdown can sit on a side that no longer has room for it.
    React.useEffect(() => {
      if (!show || usePortal) return;

      const handleResize = () => resolvePlacement();
      window.addEventListener("resize", handleResize);

      return () => window.removeEventListener("resize", handleResize);
    }, [show, usePortal, resolvePlacement]);

    // Dropdown restyled to match the shared "..." card menu: theme-aware surface, borderless rows
    // with hover:bg-muted. `isForcedLight` keeps the white-on-white variant for dropdowns that sit on
    // light modal surfaces in dark theme (isLightMode/fixedLightMode) — DS tokens follow the theme, so
    // those need explicit light colours. Shadow is omitted (the scroll wrappers below clip it); the
    // subtle border provides the definition instead.
    const isForcedLight = fixedLightMode || isLightMode;

    const dropdownContent = (
      <ul
        className={clsx(
          "flex flex-col py-1",
          // In portal mode the OUTER fixed div carries the border/rounding/scroll (so the frame stays put
          // and content scrolls inside it); on the ul the border would scroll with the list. Non-portal: the
          // ul itself is the scroll frame, so it keeps the border.
          !usePortal && "rounded-lg border border-black-300",
          isForcedLight ? "bg-trueWhite" : "bg-trueWhite dark:bg-surface-dark",
          dropdownClassName,
          // In portal mode the outer container is the scroll region (bounded to available
          // viewport space); the list scrolls itself only in the non-portal path — and only while
          // open. A scroll container on one axis computes the other axis from `visible` to `auto`
          // too, so leaving this on a closed list would give every non-portal Select a horizontal
          // scroll container it never had.
          !usePortal && show && "overflow-y-auto overscroll-none"
        )}
        ref={listRef}
        style={{
          // Only while open, so the closed list stays at its natural height and onToggle can
          // measure it. The cap now comes from the resolved placement (viewport room, lowered by
          // the call site's `maxHeight` when it passes one) rather than from `maxHeight` alone —
          // an unbounded call site used to render at full height and run off the page.
          maxHeight: !usePortal && show ? `${placement.maxHeight}px` : undefined,
        }}
      >
        {searchable && (
          // Sticky so the search bar stays pinned at the top while the options scroll (it used to scroll
          // away with the list). Repeats the list's own background so scrolled-under rows don't show through.
          <li className={clsx("sticky top-0 z-10 px-2 pt-1 pb-2 border-b border-black-300", isForcedLight ? "bg-trueWhite" : "bg-trueWhite dark:bg-surface-dark")}>
            <input
              ref={searchInputRef}
              data-testid={dataTestId ? `${dataTestId}-search` : undefined}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder ?? t("common:select.search_placeholder_default")}
              className={clsx("w-full px-2.5 py-2 text-[13px] rounded-md border border-black-300 outline-none", isForcedLight ? "bg-trueWhite text-trueBlack" : "bg-body-100 text-foreground")}
              onClick={(e) => e.stopPropagation()}
            />
          </li>
        )}
        {filteredOptions.map((option: any, index) => {
          // Ensure both selectedItem and option exist before comparing IDs to avoid false matches when null
          const isSelected = !!(selectedItem && option && selectedItem.id === option.id && !option?.disabled);

          return (
            <li
              key={index}
              data-testid={dataTestId ? `${dataTestId}-option-${option?.id}` : undefined}
              className={clsx(
                "flex items-center gap-2.5 text-[13px] px-3 py-2 transition-colors",
                isForcedLight ? "text-trueBlack" : "text-foreground",
                option?.disabled ? "cursor-not-allowed opacity-50" : clsx("cursor-pointer", isForcedLight ? "hover:bg-black/5" : "hover:bg-muted"),
                isSelected && (isForcedLight ? "bg-black/5 font-medium" : "bg-muted font-medium")
              )}
              onClick={() => !option?.disabled && onSelect(option)}
            >
              {option?.icon && <option.icon className="w-4 h-4 flex-shrink-0" />}
              {option?.img && <img src={option?.img} className="w-8 h-8 flex-shrink-0 rounded-[1px] object-cover object-center" />}
              <div translate="no" className="notranslate flex flex-col gap-[3px]">
                {option.title}
                {option?.description && <span className="text-[11px] text-grey">{option?.description}</span>}
              </div>
            </li>
          );
        })}
        {creatable && searchQuery.trim() && !filteredOptions.some((o: any) => String(o.title).toLowerCase() === searchQuery.trim().toLowerCase()) && (
          <li
            className={clsx("flex items-center gap-2 text-[13px] px-3 py-2 cursor-pointer", isForcedLight ? "text-trueBlack hover:bg-black/5" : "text-foreground hover:bg-muted")}
            onClick={() => onSelect({ id: searchQuery.trim(), title: searchQuery.trim(), value: searchQuery.trim() })}
          >
            <IconPlus className="w-3.5 h-3.5 flex-shrink-0" />
            {t("common:select.create_option", { query: searchQuery.trim() })}
          </li>
        )}
        {filteredOptions.length === 0 && searchable && !creatable && <li className="text-[13px] py-2 px-3 text-grey text-center">{t("common:select.no_results")}</li>}
      </ul>
    );

    const renderDropdown = () => {
      if (usePortal) {
        // Don't render portal at all if not showing - prevents flash at (0,0)
        if (!show) {
          return null;
        }

        // Portal dropdown - fixed position relative to viewport. Height comes from the same
        // resolved placement as the non-portal path.
        return createPortal(
          <div
            ref={dropdownRef}
            className={clsx(
              "fixed overflow-y-auto overscroll-contain rounded-lg border border-black-300",
              isForcedLight ? "bg-trueWhite" : "bg-trueWhite dark:bg-surface-dark",
              fixedLightMode ? "text-trueBlack" : isLightMode ? "text-black" : "text-white"
            )}
            style={{
              zIndex: portalZIndex,
              left: placement.left,
              ...(dropdownClassName ? { minWidth: placement.width || "auto" } : { width: placement.width || "auto" }),
              maxHeight: `${placement.maxHeight}px`,
              ...(placement.openAbove ? { bottom: window.innerHeight - placement.top } : { top: placement.top }),
            }}
          >
            {dropdownContent}
          </div>,
          document.body
        );
      }

      // Regular dropdown - absolute position within container. The wrapper is transparent and
      // overflow-hidden, so it has to be sized to whatever the capped list actually renders:
      // the natural content height, or the cap when the content is taller than it. No `+ 2` for
      // the list's borders — `measureContentHeight` already returns a border box, and the extra
      // 2px pushed a bottom-anchored (openAbove) panel 2px off its intended position.
      const contentHeight = measureContentHeight(listRef.current);
      const panelHeight = Math.min(contentHeight || placement.maxHeight, placement.maxHeight);

      return (
        <div
          ref={dropdownRef}
          className={clsx(
            "absolute z-50 transition-all duration-200 min-w-full overflow-hidden",
            fixedLightMode ? "text-trueBlack" : isLightMode ? "text-black" : "text-white",
            placement.openAbove ? "-translate-y-full" : "translate-y-full",
            show ? (placement.openAbove ? "-top-2" : "-bottom-2") : placement.openAbove ? "top-0" : "bottom-0"
          )}
          style={{
            height: show ? `${panelHeight}px` : 0,
          }}
        >
          {dropdownContent}
        </div>
      );
    };

    const handleToggleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle();
      },
      [onToggle]
    );

    return (
      <div className="flex-1 relative" ref={containerRef}>
        <div
          className={clsx(
            "cursor-pointer flex items-center gap-1 w-full max-w-[160px] h-[38px] pl-2.5 pr-1.5 text-white border border-black-300 rounded-[5px]",
            disabled ? "pointer-events-none opacity-50" : "",
            className
          )}
          onClick={handleToggleClick}
          data-testid={dataTestId}
        >
          {selectedItem?.icon && <selectedItem.icon className="w-4 h-4 flex-shrink-0 text-trueBlack dark:text-white" />}
          <span
            translate="no"
            className={clsx("notranslate flex-1 min-w-0 truncate text-bodySm", ((!selectedItem && placeholder) || (mutedId !== undefined && selectedItem?.id === mutedId)) && "text-grey")}
          >
            {titlePrefix}
            {selectedItem?.title ?? placeholder ?? ""}
          </span>
          {/* Chevron is a bare icon (no boxed background / divider), tucked close to the right edge. */}
          <IconChevronDown className="w-[18px] h-[18px] flex-shrink-0 text-trueBlack dark:text-white" />
        </div>
        {renderDropdown()}
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
